import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { CompliantType, ModelError_Item } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { ControllerProps, FieldErrors, useForm } from 'react-hook-form';
import * as yup from 'yup';

import VendorAccountDetails from '~components/Common/VendorAccountDetails';
import { CompliancePanel } from '~components/CompliancePanel/CompliancePanel';
import { driverFormValidationSchema } from '~components/DriverForm/driverFormValidationSchema';
import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { CompanySelectorFormField } from '~components/FormFields/CompanySelectorFormField';
import { PhoneCodeFlagInput } from '~components/FormFields/PhoneCodeFlagInput';
import { TextFormField } from '~components/FormFields/TextFormField';
import { UserFormStateChangeProps } from '~components/UserForm/UserForm';
import {
  userEmailSchema,
  userPhoneSchema,
} from '~components/UserForm/userFormValidationSchema';
import { applyInviteTypeErrors } from '~components/UserInvite/inviteTypeErrors';
import UserInviteTypeSelector, {
  InviteType,
} from '~components/UserInvite/UserInviteTypeSelector';
import { AddressItem } from '~hooks/useAddress/models/AddressItem';
import { SupportedAddressProvider } from '~hooks/useAddress/models/AddressProvider';
import { useCompany } from '~hooks/useCompany/useCompany';
import {
  DriverContext,
  StagedComplianceDoc,
} from '~hooks/useComplianceDocuments/checklist';
import { EquipmentTypeahead } from '~hooks/useEquipment/models';
import useEquipment from '~hooks/useEquipment/useEquipment';
import { useGeocode } from '~hooks/useGeocode/useGeocode';
import { BasicLabel } from '~hooks/useLabels/models';
import useLabels from '~hooks/useLabels/useLabels';
import { getEquipmentLabel } from '~pages/Settings/UserManagement/EquipmentSelector';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import { allowedTimezones } from '~utils/dateTime';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

interface DriverFormProps {
  errors?: ModelError_Item[];
  isManagedDriver?: boolean;
  onFormStateChange: ({
    isValid,
    isDirty,
    hasValidEmail,
    hasValidPhone,
  }: UserFormStateChangeProps) => void;
  inviteTypes?: InviteType[];
  inviteTypeError?: string;
  onInviteTypesChange?: (value: InviteType[]) => void;
  isUpdating?: boolean;
  companyId?: string;
  managedCompanyId?: string;
  driverContext?: DriverContext;
  onStagedDocsChange?: (docs: StagedComplianceDoc[]) => void;
}

export type DriverFormValues = yup.InferType<typeof driverFormValidationSchema>;

export interface DriverFormHandler {
  submit: (callback: (data: DriverFormValues | undefined) => void) => void;
}

const validateEmail = (email: Nullable<string>) => {
  let isValid = true;

  try {
    const input = email || '';

    if (input.length) {
      userEmailSchema.validateSync(input);
    } else {
      isValid = false;
    }
  } catch {
    isValid = false;
  }

  return isValid;
};

const validatePhone = (phone: Nullable<string>) => {
  let isValid = true;

  try {
    const input = phone || '';

    if (input.length) {
      userPhoneSchema.validateSync(input);
    } else {
      isValid = false;
    }
  } catch {
    isValid = false;
  }

  return isValid;
};

const getErrorMapping = (fieldName: string, defaultMessage: string) => {
  switch (fieldName) {
    case 'legal_name': {
      return {
        field: 'firstName',
        message: defaultMessage,
      };
    }
    case 'name': {
      return {
        field: 'firstName',
        message: defaultMessage,
      };
    }
    case 'base': {
      return {
        field: 'firstName',
        message: defaultMessage,
      };
    }
    default:
      return {
        field: fieldName,
        message: defaultMessage,
      };
  }
};

export const DriverForm = forwardRef<DriverFormHandler, DriverFormProps>(
  (
    {
      onFormStateChange,
      isManagedDriver,
      errors: errorsProp,
      inviteTypes,
      inviteTypeError,
      onInviteTypesChange,
      isUpdating,
      companyId: scopedCompanyId,
      managedCompanyId,
      driverContext = 'shared',
      onStagedDocsChange,
    },
    ref,
  ) => {
    const { userStore } = useStores();
    const { getCompanyById } = useCompany();
    const currentCompany = userStore.currentCompanies[0] || userStore.userCompany;
    const companyId = scopedCompanyId || getEffectiveUserCompanyId(userStore);
    const [tmpEquipment, setTmpEquipment] = useState<string>('');
    const [isEquipmentLoading] = useState(false);
    const [isCompanyLoading, setIsCompanyLoading] = useState(false);
    const { getEquipmentByCompanyIdTypeahead, getEquipmentTypesByCompanyIdTypeahead } =
      useEquipment();
    const getEquipmentByCompanyIdTypeaheadSafe = useCallback(
      async (options: Parameters<typeof getEquipmentByCompanyIdTypeahead>[0]) => {
        try {
          return await getEquipmentByCompanyIdTypeahead(options);
        } catch {
          // Error is already handled in the hook
          return { data: [], pagination: undefined };
        }
      },
      [getEquipmentByCompanyIdTypeahead],
    );
    const getEquipmentTypesByCompanyIdTypeaheadSafe = useCallback(
      async (options: Parameters<typeof getEquipmentTypesByCompanyIdTypeahead>[0]) => {
        try {
          return await getEquipmentTypesByCompanyIdTypeahead(options);
        } catch {
          // Error is already handled in the hook
          return { data: [], pagination: undefined };
        }
      },
      [getEquipmentTypesByCompanyIdTypeahead],
    );
    const { fetchLabelsTypeahead } = useLabels();

    useEffect(() => {
      const fetchCompany = async () => {
        if (!companyId) {
          return;
        }

        setIsCompanyLoading(true);
        try {
          const company = await getCompanyById({ id: companyId });
          if (company?.timeZone) {
            setValue('timeZone', company.timeZone);
          }
        } finally {
          setIsCompanyLoading(false);
        }
      };

      fetchCompany();
    }, [companyId]);

    useEffect(() => {
      if (errorsProp) {
        for (const error of errorsProp) {
          const newErrorField = getErrorMapping(error.field, error.message);
          if (newErrorField.field && newErrorField.message) {
            setError(newErrorField.field as keyof ReturnType<typeof setError>, {
              type: 'manual',
              message: newErrorField.message,
            });
          }
        }
      }
    }, [errorsProp]);

    const defaultValues = {
      company: currentCompany,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: null,
      timeZone: userStore.userCompany?.timeZone || '',
      notes: '',
      equipment: {
        id: '',
        name: '',
      },
      equipmentType: {
        id: '',
        name: '',
      },
      createInternalEquipment: false,
      accountName: '',
      accountExternalId: '',
      labels: [],
    };

    const {
      control,
      handleSubmit,
      watch,
      trigger,
      setValue,
      setError,
      clearErrors,
      formState: { errors, isValid, isDirty },
    } = useForm({
      resolver: yupResolver(driverFormValidationSchema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues,
    });

    const watchedEmail = watch('email');
    const watchedPhone = watch('phone');
    const watchedCompany = watch('company');

    const hasValidEmail = validateEmail(watchedEmail);
    const hasValidPhone = validatePhone(watchedPhone);

    const formCompanyId = watchedCompany?.id || companyId;

    const equipment = watch('equipment');

    useEffect(() => {
      onFormStateChange({ isValid, isDirty, hasValidEmail, hasValidPhone });
    }, [isValid, isDirty, hasValidEmail, hasValidPhone]);

    const addressProvider = SupportedAddressProvider.GOOGLE_MAPS_PLACES_TEXT_SEARCH;
    const { getPlaces, getAddressItemByGoogleMapsPlaceId } = useGeocode({
      addressProvider,
    });

    const handleAddressChange = useCallback(
      async (item: AddressItem) => {
        if (addressProvider === SupportedAddressProvider.GOOGLE_MAPS_PLACES_TEXT_SEARCH) {
          return await getAddressItemByGoogleMapsPlaceId(
            item.placeId,
            item.fullAddress ?? undefined,
          );
        }
        return item;
      },
      [getAddressItemByGoogleMapsPlaceId],
    );

    const onInputEquipment = useCallback((event: React.SyntheticEvent, value: string) => {
      setTmpEquipment(value);
    }, []);

    const selectNewEquipment = useCallback(() => {
      setValue('equipment', {
        id: '',
        name: tmpEquipment,
      });
      trigger('equipment');
    }, [tmpEquipment, setValue, trigger]);

    const routeInviteTypeError = () =>
      applyInviteTypeErrors({
        clearEmailError: () => clearErrors('email'),
        email: watchedEmail,
        hasValidEmail,
        hasValidPhone,
        inviteTypes: inviteTypes ?? [],
        setEmailError: (message) => setError('email', { type: 'manual', message }),
        setPhoneError: (message) => setError('phone', { type: 'manual', message }),
      });

    useImperativeHandle(
      ref,
      () => ({
        submit(callback) {
          handleSubmit(
            (data) => {
              if (routeInviteTypeError()) {
                callback(undefined);
                return;
              }
              callback(data as DriverFormValues);
            },
            () => {
              routeInviteTypeError();
              callback(undefined);
            },
          )();
        },
      }),
      [handleSubmit, routeInviteTypeError],
    );

    useEffect(() => {
      if (equipment?.id && equipment instanceof EquipmentTypeahead) {
        setValue('equipmentType', equipment.equipmentType);
      }
      if (!equipment) {
        setValue('equipmentType', { id: '', name: '' });
      }
    }, [equipment]);

    return (
      <Box
        component="form"
        sx={{ display: 'flex', flexDirection: 'column', width: '100%', flexGrow: 1 }}
      >
        <Grid container spacing={2}>
          {!isManagedDriver && (
            <Grid size={{ xs: 12 }}>
              <CompanySelectorFormField
                sx={{ width: '100%', minWidth: '100%' }}
                name="company"
                isRequired={true}
                disabled={false}
                errors={errors}
                control={control}
              />
            </Grid>
          )}

          <Grid size={{ xs: 12 }}>
            <VendorAccountDetails control={control} errors={errors} />
          </Grid>

          <Grid size={{ xs: 6 }}>
            <TextFormField
              control={control}
              errors={errors}
              name="firstName"
              label={t('form_fields.first_name')}
              isRequired={true}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextFormField
              control={control}
              errors={errors}
              name="lastName"
              label={t('form_fields.last_name')}
              isRequired={true}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <AutocompleteAsyncFormField
              control={control}
              errors={errors}
              clearable
              name="equipment"
              label={t('form_fields.truck_number_name')}
              getValue={(item) => item.id}
              getLabel={(item) =>
                item instanceof EquipmentTypeahead ? getEquipmentLabel(item) : item.name
              }
              asyncCallback={getEquipmentByCompanyIdTypeaheadSafe}
              extraRequestOptions={{
                companyId,
                managedCompanyId,
              }}
              onInput={onInputEquipment}
              noOptionsText={
                tmpEquipment ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      p: 0,
                    }}
                  >
                    <Chip
                      size="small"
                      variant="filled"
                      color="primary"
                      onClick={() => {
                        selectNewEquipment();
                        setValue('createInternalEquipment', true);
                      }}
                      disabled={isEquipmentLoading}
                      sx={{ cursor: 'pointer', width: 'fit-content' }}
                      label={t('form_fields.create_internal_truck')}
                    />
                    <Chip
                      size="small"
                      variant="filled"
                      color="primary"
                      onClick={() => {
                        selectNewEquipment();
                        setValue('createInternalEquipment', false);
                      }}
                      disabled={isEquipmentLoading}
                      sx={{ cursor: 'pointer', width: 'fit-content' }}
                      label={t('form_fields.create_external_truck')}
                    />
                    <Typography variant="body2">
                      {t('form_fields.new_truck_hint')}
                    </Typography>
                  </Box>
                ) : (
                  t('form_fields.no_options')
                )
              }
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <AutocompleteAsyncFormField
              control={control}
              errors={errors}
              name="equipmentType"
              isRequired={!!equipment?.name}
              label={t('form_fields.truck_type')}
              clearable={false}
              getValue={(item) => item.id}
              getLabel={(item) => item.name || ''}
              asyncCallback={getEquipmentTypesByCompanyIdTypeaheadSafe}
              extraRequestOptions={{
                companyId,
                managedCompanyId,
              }}
              disabled={!equipment?.name || !!equipment?.id}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <PhoneCodeFlagInput
              control={control as unknown as ControllerProps['control']}
              errors={errors as unknown as FieldErrors}
              isRequired={!!inviteTypes?.includes('sms')}
              name="phone"
              label={t('form_fields.phone')}
            />
            <Typography component="p" variant="body2">
              {t('form_fields.contact_required')}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextFormField
              control={control}
              errors={errors}
              isRequired={!!inviteTypes?.includes('email')}
              name="email"
              type="email"
              label={t('form_fields.email')}
            />
          </Grid>
          {onInviteTypesChange && (
            <Grid size={{ xs: 12 }}>
              <UserInviteTypeSelector
                email={watchedEmail}
                error={inviteTypeError}
                isDisabled={isUpdating}
                isEmailEnabled={hasValidEmail}
                isPhoneEnabled={hasValidPhone}
                onChange={onInviteTypesChange}
                phone={watchedPhone}
                titleKey="invite_type.title_driver"
                value={inviteTypes ?? []}
              />
            </Grid>
          )}
          <Grid size={{ xs: 6 }}>
            <AutocompleteFormField
              control={control}
              name="timeZone"
              errors={errors}
              list={allowedTimezones}
              label={t('form_fields.time_zone')}
              isRequired={true}
              clearable={false}
              getValue={(item) => item}
              getLabel={(item) => item}
              disabled={isUpdating || isCompanyLoading}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextFormField
              control={control}
              errors={errors}
              name="notes"
              multiline={true}
              rows={2}
              label={t('form_fields.notes')}
              isRequired={false}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <AutocompleteAsyncFormField
              control={control}
              errors={errors}
              name="address"
              getLabel={(item) => item.nameWithAddress}
              getValue={(item) => item.placeId}
              label={t('form_fields.address')}
              placeholder="Start typing to search"
              asyncCallback={getPlaces}
              onSelect={handleAddressChange}
              debounceTime={500}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <AutocompleteAsyncFormField
              asyncCallback={fetchLabelsTypeahead}
              control={control}
              errors={errors}
              extraRequestOptions={{ companyId }}
              getLabel={(label: BasicLabel) => label.name}
              getValue={(label: BasicLabel) => label.id}
              label={t('form_fields.labels')}
              limitTags={3}
              multiple
              name="labels"
            />
          </Grid>

          {onStagedDocsChange && (
            <Grid size={{ xs: 12 }}>
              <Box sx={{ mt: 1 }}>
                <CompliancePanel
                  compliantType={CompliantType.USER}
                  companyId={formCompanyId}
                  managedCompanyId={managedCompanyId}
                  driverContext={driverContext}
                  stagedMode
                  onStagedDocsChange={onStagedDocsChange}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  },
);

DriverForm.displayName = 'DriverForm';
